import React, { useState, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
    Box,
    Stack,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';

import { useSnackbar } from 'utils/hooks/useSnackbar';
import { ProjectSelect } from "utils/projectSelector/ProjectSelect";
import { DestinationSelect } from "utils/projectSelector/DestinationSelect";
import { LotSelect } from "utils/projectSelector/LotSelect";
import { PhaseSelect } from "utils/projectSelector/PhaseSelect";
import { UnitSelect } from "utils/projectSelector/UnitSelect";
import { CalculationSelect } from "utils/projectSelector/CalculationSelect";
import {
    calculationIdAtom,
    destinationIdAtom,
    lotIdAtom,
    phaseIdAtom,
    projectIdAtom,
    stageConfigAtom,
    unitIdAtom
} from 'atoms/Rtool/stageConfigAtoms';
import { authTokenAtom } from 'atoms/authAtoms';
import { powertrainAtom } from 'atoms/powertrainAtom';
import { rCalcSetMtgAtom } from 'atoms/Rtool/mtgAtoms';
import { useCalculationSet } from 'utils/hooks/useRToolTree';
import { patchCalculationSetDescription, deleteCalculationSet } from 'api/submitRtool';
import { toUserMessage } from 'utils/apiErrorMessage';
import { useIsCopyJobBlocking, useRestoreCopyJobOnMount } from 'utils/hooks/useCopyJobBlocking';

export const ExistingProjects: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const config = useAtomValue(stageConfigAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const setProjectId = useSetAtom(projectIdAtom);
    const setRCalcSetMtg = useSetAtom(rCalcSetMtgAtom);
    const { showSuccess, showError } = useSnackbar();
    const { data: calcSet, loading: calcSetLoading } = useCalculationSet(authToken, config);

    // この画面（stageConfigAtom）はDataRegistration（copyToConfigAtom）とは独立した選択のため、
    // ここでも別途復元を呼ぶ（issue #43 AC6）。
    useRestoreCopyJobOnMount(powertrain, config);
    const isCopyBlocking = useIsCopyJobBlocking(powertrain, config);

    useEffect(() => {
        if (calcSet) setRCalcSetMtg(calcSet.mtg);
    }, [calcSet, setRCalcSetMtg]);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editDescription, setEditDescription] = useState('');
    const [updating, setUpdating] = useState(false);

    useEffect(() => {
        setEditDescription(calcSet?.description ?? '');
    }, [calcSet]);

    const canDelete = !!(
        config.project_id &&
        config.destination_id &&
        config.lot_id &&
        config.phase_id &&
        config.unit_id &&
        config.calculation_id
    ) && !isCopyBlocking;

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            const message = await deleteCalculationSet(authToken, config, powertrain);
            showSuccess(message);
            setConfirmOpen(false);
            setProjectId('');
        } catch (e) {
            showError(toUserMessage(e));
        } finally {
            setDeleting(false);
        }
    };

    const handleUpdateDescription = async () => {
        setUpdating(true);
        try {
            const message = await patchCalculationSetDescription(authToken, config, editDescription, powertrain);
            showSuccess(message);
        } catch (e) {
            showError(toUserMessage(e));
        } finally {
            setUpdating(false);
        }
    };

    const selectedPath = [
        config.project_id,
        config.destination_id,
        config.lot_id,
        config.phase_id,
        config.unit_id,
        config.calculation_id
    ].filter(id => id).join(' / ');

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>
                R 検討ツール 計算セット選択
            </Typography>

            {selectedPath ? (
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    選択中の既存計算セット：{selectedPath}
                </Typography>
            ) : (
                <Typography variant="subtitle1" sx={{ mb: 2, color: 'error.main' }}>
                    既存計算セットが選択されていません。
                </Typography>
            )}

            {isCopyBlocking && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    コピー実行中のため操作できません。
                </Typography>
            )}

            <Box sx={{ mb: 2 }}>
                <Button
                    variant="outlined"
                    color="error"
                    disabled={!canDelete}
                    onClick={() => setConfirmOpen(true)}
                >
                    計算セットを削除
                </Button>
            </Box>

            <Stack
                component="form"
                spacing={2}
                noValidate
                autoComplete="off"
                direction="column"
                alignItems="stretch"
            >
                <ProjectSelect atom={projectIdAtom} />
                <DestinationSelect atom={destinationIdAtom} config={config} />
                <LotSelect atom={lotIdAtom} config={config} />
                <PhaseSelect atom={phaseIdAtom} config={config} />
                <UnitSelect atom={unitIdAtom} config={config} />
                <CalculationSelect atom={calculationIdAtom} config={config} />
            </Stack>

            <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {calcSetLoading ? (
                    <CircularProgress size={24} />
                ) : calcSet ? (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            マウント数 (mtg)
                        </Typography>
                        <Typography sx={{ mb: 2 }}>
                            {calcSet.mtg}
                        </Typography>

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Description
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={5}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={handleUpdateDescription}
                            disabled={updating || editDescription === calcSet.description || isCopyBlocking}
                        >
                            {updating ? <CircularProgress size={20} /> : '説明を更新'}
                        </Button>
                    </>
                ) : (
                    <Typography color="text.secondary">
                        {config.calculation_id
                            ? '計算セットの情報を取得できませんでした。'
                            : '計算セットを選択してください。'}
                    </Typography>
                )}
            </Box>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>削除の確認</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {config.calculation_id} を削除しますか？
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
                        キャンセル
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" disabled={deleting || isCopyBlocking}>
                        {deleting ? <CircularProgress size={20} /> : '確認'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
